import type {
  Analytics,
  DocumentDetail,
  Health,
  QueuePage,
  SampleKind,
} from "./types";

/**
 * One origin, so no base URL and no CORS: in development Vite proxies
 * `/api`, and in production FastAPI serves these files itself.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);
  if (!response.ok) {
    // The API says what went wrong in `detail`. Showing "500" and
    // swallowing that is how a support request becomes a debugging
    // session.
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* a non-JSON error body is still an error */
    }
    throw new Error(detail);
  }
  return (await response.json()) as T;
}

export const api = {
  health: () => request<Health>("/api/health"),

  queue: (params: { status?: string; source?: string; q?: string; offset?: number }) => {
    const search = new URLSearchParams();
    if (params.status) search.set("status", params.status);
    if (params.source) search.set("source", params.source);
    if (params.q) search.set("q", params.q);
    if (params.offset) search.set("offset", String(params.offset));
    return request<QueuePage>(`/api/documents?${search.toString()}`);
  },

  document: (id: string) => request<DocumentDetail>(`/api/documents/${id}`),

  /**
   * The ready-made documents, and running one.
   *
   * The list comes from the server rather than being spelled out here,
   * so a button can never promise something the sample does not do.
   */
  samples: () => request<SampleKind[]>("/api/samples"),

  runSample: (kind: string) =>
    request<DocumentDetail>(`/api/samples/${kind}`, { method: "POST" }),

  upload: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<DocumentDetail>("/api/documents", { method: "POST", body });
  },

  readAgain: (id: string) =>
    request<DocumentDetail>(`/api/documents/${id}/extractions`, { method: "POST" }),

  approve: (id: string) =>
    request<DocumentDetail>(`/api/documents/${id}/approve`, { method: "POST" }),

  analytics: (params: { source?: string; currency?: string; vendor?: string }) => {
    const search = new URLSearchParams();
    if (params.source) search.set("source", params.source);
    if (params.currency) search.set("currency", params.currency);
    if (params.vendor) search.set("vendor", params.vendor);
    return request<Analytics>(`/api/analytics?${search.toString()}`);
  },

  /**
   * Delete a document, its readings and the stored file.
   *
   * Not routed through `request`, which parses a JSON body: a 204 has
   * none, and calling .json() on it throws. The endpoint deliberately
   * answers 204 for a document that is already gone, so a second click
   * is a success rather than an error a person has to interpret.
   */
  remove: async (id: string): Promise<void> => {
    const response = await fetch(`/api/documents/${id}`, { method: "DELETE" });
    if (!response.ok) {
      let detail = `${response.status} ${response.statusText}`;
      try {
        const body = await response.json();
        if (body?.detail) detail = String(body.detail);
      } catch {
        /* a non-JSON error body is still an error */
      }
      throw new Error(detail);
    }
  },

  exportUrl: (id: string) => `/api/documents/${id}/export.json`,
  fileUrl: (id: string) => `/api/documents/${id}/file`,
};
